import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ChevronLeft, User, Building2, Plane, GraduationCap, FileText, Download, Search } from "lucide-react";
import { ExpiryBanner, ExpiryDocLink } from "../components/ExpiryBanner";
import DocViewerModal from "../components/DocViewerModal";

const moduleBadge = (m) => ({
  "B2C": "bg-orange-50 text-orange-800 border-orange-200",
  "B2B": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "Immigration": "bg-teal-50 text-teal-800 border-teal-200",
}[m] || "bg-slate-50 text-slate-700 border-slate-200");

export function ClientSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!q.trim()) { setRows([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/clients/search?q=${encodeURIComponent(q.trim())}`); setRows(r.data); }
      catch { setRows([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Client 360</h1>
        <p className="text-sm text-slate-500 mt-1">One profile per person across B2C admissions, B2B partner referrals, and Immigration files.</p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="client-search-input" placeholder="Search name, email, phone, UCI, ID…" className="pl-9" autoFocus value={q} onChange={e=>setQ(e.target.value)} />
      </div>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {rows.length === 0 && q.trim() && <div className="p-6 text-slate-500 text-sm">No matches.</div>}
        {rows.length === 0 && !q.trim() && <div className="p-6 text-slate-500 text-sm">Start typing to search…</div>}
        {rows.map(r => (
          <button key={r.id} onClick={()=>nav(`/clients/${r.id}`)} className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center justify-between">
            <div>
              <div className="font-medium text-slate-900">{r.full_name}</div>
              <div className="text-xs text-slate-500">{r.email} · {r.phone} {r.uci ? `· UCI ${r.uci}` : ""}</div>
            </div>
            <div className="flex gap-1">
              {(r.used_in || []).map(m => <span key={m} className={`stage-badge ${moduleBadge(m)}`}>{m}</span>)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Client360() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [docViewer, setDocViewer] = useState(null);

  useEffect(() => { api.get(`/clients/${id}/360`).then(r=>setData(r.data)); }, [id]);

  // Find the earliest upcoming expiry across all applications
  const earliestExpiry = useMemo(() => {
    if (!data) return null;
    const items = [];
    (data.b2c_applications || []).forEach(a => a.visa_expiry && items.push({ date: a.visa_expiry, source: `B2C ${a.application_id}`, docUrl: a.expiry_doc_url, module: "B2C" }));
    (data.immigration_applications || []).forEach(a => a.status_expiry_date && items.push({ date: a.status_expiry_date, source: `Immigration ${a.application_number}`, docUrl: a.expiry_doc_url, module: "Immigration" }));
    if (!items.length) return null;
    items.sort((a, b) => new Date(a.date) - new Date(b.date));
    return items[0];
  }, [data]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const c = data.client;

  const download = async (d) => {
    let url;
    if (d._module === "B2C") url = `/b2c/files/${d.id}/download`;
    else if (d._module === "Immigration") url = `/immigration/files/${d.id}/download`;
    else url = `/files/${d.id}/download`;
    const r = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(r.data);
    const link = document.createElement("a"); link.href = objUrl; link.download = d.original_filename || "file"; link.click();
    URL.revokeObjectURL(objUrl);
  };

  return (
    <div className="space-y-6">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ChevronLeft className="w-4 h-4"/>Client search</Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center"><User className="w-7 h-7"/></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="heading-font text-2xl font-bold text-slate-900">{c.full_name}</h1>
              {c.student_id && <span className="mono text-xs text-slate-500">{c.student_id}</span>}
            </div>
            <div className="text-sm text-slate-600 mt-1">{c.email} · {c.phone}{c.uci && ` · UCI ${c.uci}`}</div>
            <div className="text-xs text-slate-500 mt-1">{c.address || c.country_of_origin || "—"}</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(c.used_in || []).map(m => <span key={m} className={`stage-badge ${moduleBadge(m)}`}>{m}</span>)}
              <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">B2C: {data.counts.b2c}</span>
              <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">B2B: {data.counts.b2b}</span>
              <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">Immigration: {data.counts.immigration}</span>
              <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">Documents: {data.counts.documents}</span>
            </div>
          </div>
        </div>
      </div>

      {earliestExpiry && (
        <ExpiryBanner
          expiryDate={earliestExpiry.date}
          expiryLabel={`Nearest Expiry · ${earliestExpiry.source}`}
          contextChips={[c.uci && { label: "UCI", value: c.uci }].filter(Boolean)}
          docUrl={earliestExpiry.docUrl}
          onOpenDoc={(url, title) => setDocViewer({ url, title })}
        />
      )}

      <Tabs defaultValue="admissions">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="admissions">B2C Admissions ({data.counts.b2c})</TabsTrigger>
          <TabsTrigger value="partner">Partner Referrals ({data.counts.b2b})</TabsTrigger>
          <TabsTrigger value="immigration">Immigration ({data.counts.immigration})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({data.counts.documents})</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="admissions">
          <List rows={data.b2c_applications} empty="No B2C applications." columns={[
            ["ID", a => <Link to={`/b2c/applications/${a.id}`} className="mono text-xs text-orange-600 hover:underline">{a.application_id}</Link>],
            ["College", a => a.college_name],
            ["Program", a => a.program_name],
            ["Stage", a => <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">{a.stage}</span>],
            ["Filing", a => a.filing_status || "—"],
            ["Visa Expiry", a => a.visa_expiry ? <span className="text-xs">{fmtDate(a.visa_expiry)} {a.expiry_doc_url && <ExpiryDocLink url={a.expiry_doc_url} onOpen={(u,t)=>setDocViewer({url:u,title:t})} testId={`c360-b2c-doc-${a.application_id}`} />}</span> : "—"],
            ["Created", a => fmtDate(a.created_at)],
          ]} icon={GraduationCap} />
        </TabsContent>

        <TabsContent value="partner">
          <List rows={data.b2b_applications} empty="No B2B partner referrals for this person." columns={[
            ["ID", a => <Link to={`/applications/${a.id}`} className="mono text-xs text-orange-600 hover:underline">{a.application_id}</Link>],
            ["Partner", a => a.partner_name || "—"],
            ["College", a => a.college],
            ["Program", a => a.program],
            ["Stage", a => <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">{a.stage}</span>],
            ["Received", a => fmtDate(a.date_received)],
          ]} icon={Building2} />
        </TabsContent>

        <TabsContent value="immigration">
          <List rows={data.immigration_applications} empty="No immigration files." columns={[
            ["ID", a => <Link to={`/immigration/applications/${a.id}`} className="mono text-xs text-orange-600 hover:underline">{a.application_number}</Link>],
            ["Type", a => a.app_type_name],
            ["Status", a => <span className="stage-badge bg-slate-50 text-slate-700 border-slate-200">{a.status}</span>],
            ["Officer", a => a.assigned_officer_name || "Unassigned"],
            ["Expiry", a => a.status_expiry_date ? <span className="text-xs">{fmtDate(a.status_expiry_date)} {a.expiry_doc_url && <ExpiryDocLink url={a.expiry_doc_url} onOpen={(u,t)=>setDocViewer({url:u,title:t})} testId={`c360-imm-doc-${a.application_number}`} />}</span> : "—"],
            ["Priority", a => a.priority],
          ]} icon={Plane} />
        </TabsContent>

        <TabsContent value="documents">
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {data.documents.length === 0 && <div className="p-6 text-sm text-slate-500">No documents.</div>}
            {data.documents.map(d => (
              <div key={`${d._module}-${d.id}`} data-testid={`c360-doc-${d.id}`} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 text-slate-400"/>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.original_filename}</div>
                    <div className="text-xs text-slate-500">{d.doc_type} · {fmtDate(d.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`stage-badge ${moduleBadge(d._module)}`}>{d._module}</span>
                  <Button variant="outline" size="sm" onClick={()=>download(d)}><Download className="w-4 h-4 mr-1"/>Download</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {data.activity.length === 0 && <div className="p-6 text-sm text-slate-500">No activity yet.</div>}
            {data.activity.map(x => (
              <div key={x.id} className="p-3 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900">{x.actor_name || "System"}</span>
                  <span className={`stage-badge ${moduleBadge(x._module)}`}>{x._module}</span>
                  <span className="text-xs text-slate-500">· {x.action}</span>
                  <span className="text-xs text-slate-400 ml-auto">{fmtDate(x.created_at)}</span>
                </div>
                {(x.old_value != null || x.new_value != null || x.details) && (
                  <div className="text-xs text-slate-600 mt-1">
                    {x.details && <span>{x.details}</span>}
                    {x.old_value != null && <><span className="text-slate-400">from </span><span className="mono">{String(x.old_value)}</span> </>}
                    {x.new_value != null && <><span className="text-slate-400">→ </span><span className="mono">{String(x.new_value)}</span></>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <DocViewerModal open={!!docViewer} onOpenChange={(v)=>{ if (!v) setDocViewer(null); }} url={docViewer?.url} title={docViewer?.title} />
    </div>
  );
}

const List = ({ rows, empty, columns, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
        <tr>{columns.map(([h]) => <th key={h} className="text-left px-4 py-2">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-t border-slate-100 hover:bg-slate-50">
            {columns.map(([h, fn], j) => <td key={h} className="px-4 py-2">{fn(r)}</td>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={columns.length} className="text-center py-6 text-slate-500 flex items-center justify-center gap-2"><Icon className="w-4 h-4"/>{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);
